/**
  * Wavefront OBJ + MTL writer.
 *
 * The standalone tool this app was ported from only wrote GLB. OBJ is here
 * because it is the format that still opens in everything — old DCC tools,
 * 3D printers' slicers, engines with no glTF importer — and because it costs
 * one text file.
 *
 * Same input contract and the same liftability rule as the glTF writer: plain
 * `MeshPiece[]` in, strings out, no THREE and no app state.
 *
 * Two format facts worth naming, because both are silent corruptions:
 * • OBJ indices are **1-based** and **file-global**, not per-object. The
 *   running `base` below is what keeps a second piece from referencing the
 *   first one's vertices.
 * • OBJ has no vertex colours in the base spec. Colour therefore always comes
 *   from the MTL, so `vertexColors` is honoured by collapsing to one material
 *   rather than by baking — an OBJ with per-vertex colour would be read as
 *   white by most importers.
 */
import { centerOffset, transformPoint, worldBox } from "./bbox.mjs";
import { hexToLinear } from "./color.mjs";
import {
  DEFAULT_EXPORT_OPTIONS,
  type MeshExportInfo,
  type MeshPiece,
  type MeshExportOptions,
} from "./types";

const f = (v: number) => (Math.round(v * 1e6) / 1e6).toString();

export function buildOBJ(
  pieces: MeshPiece[],
  options?: Partial<MeshExportOptions>
): { obj: string; mtl: string; info: MeshExportInfo } {
  const opts: MeshExportOptions = { ...DEFAULT_EXPORT_OPTIONS, ...(options || {}) };
  if (!pieces.length) throw new Error("Nothing to export");

  const gs = opts.scale;
  const off = opts.center
    ? centerOffset(worldBox(pieces.map((p) => ({ box: p.box, pos: p.pos, rotY: p.rotY, scale: p.scale }))))
    : [0, 0, 0];

  /* ---- materials ---- */
  const slotForHex = new Map<string, string>();
  for (const p of pieces)
    for (const g of p.groups) if (!slotForHex.has(g.color)) slotForHex.set(g.color, g.slot);

  const matName = new Map<string, string>();
  const used = new Set<string>();
  function nameFor(hex: string) {
    const found = matName.get(hex);
    if (found) return found;
    // slot names are the readable option, but two slots can share a hex, so
    // collisions get a numeric suffix rather than silently merging
    let base = slotForHex.get(hex) || `color_${matName.size}`;
    let n = base;
    let i = 2;
    while (used.has(n)) n = `${base}_${i++}`;
    used.add(n);
    matName.set(hex, n);
    return n;
  }

  const obj: string[] = [];
  const objName = opts.name.replace(/[^\w.-]+/g, "_");
  obj.push(`# ${opts.generator}`);
  obj.push(`mtllib ${objName}.mtl`);

  let base = 1; // OBJ vertex indices are 1-based and global
  let totalVerts = 0;
  let totalPrims = 0;
  const grouped = opts.mode === "merged";
  if (grouped) obj.push("o structure");

  for (const p of pieces) {
    if (!grouped) obj.push(`o ${p.kind}_${p.id}`);
    const cos = Math.cos(p.rotY);
    const sin = Math.sin(p.rotY);
    const n = p.position.length / 3;

    for (let i = 0; i < n; i++) {
      const v = transformPoint(
        [p.position[i * 3], p.position[i * 3 + 1], p.position[i * 3 + 2]],
        p.pos, p.rotY, p.scale
      );
      obj.push(`v ${f((v[0] + off[0]) * gs)} ${f((v[1] + off[1]) * gs)} ${f((v[2] + off[2]) * gs)}`);
    }
    for (let i = 0; i < n; i++) {
      const nx = p.normal[i * 3], ny = p.normal[i * 3 + 1], nz = p.normal[i * 3 + 2];
      obj.push(`vn ${f(nx * cos + nz * sin)} ${f(ny)} ${f(-nx * sin + nz * cos)}`);
    }

    for (const gr of p.groups) {
      obj.push(`usemtl ${opts.vertexColors ? "palette" : nameFor(gr.color)}`);
      // geometry is non-indexed, so every three consecutive vertices are a face
      for (let i = gr.start; i + 2 < gr.start + gr.count; i += 3) {
        const a = base + i, b = base + i + 1, c = base + i + 2;
        obj.push(`f ${a}//${a} ${b}//${b} ${c}//${c}`);
      }
      totalPrims++;
    }
    base += n;
    totalVerts += n;
  }

  /* ---- MTL ---- */
  const mtl: string[] = [`# ${opts.generator}`];
  if (opts.vertexColors) {
    mtl.push("newmtl palette", "Kd 1 1 1", "Ks 0 0 0", "d 1", "illum 1", "");
    if (!matName.size) nameFor(pieces[0].groups[0].color);
  } else {
    matName.forEach((name, hex) => {
      // Kd in OBJ is conventionally linear, matching the glTF baseColorFactor
      const lin = hexToLinear(hex);
      const slot = slotForHex.get(hex);
      mtl.push(`newmtl ${name}`);
      mtl.push(`Kd ${f(lin[0])} ${f(lin[1])} ${f(lin[2])}`);
      mtl.push("Ks 0 0 0");
      if (slot === "flame") mtl.push(`Ke ${f(lin[0])} ${f(lin[1])} ${f(lin[2])}`);
      mtl.push(slot === "water" ? "d 0.8" : "d 1");
      mtl.push("illum 1", "");
    });
  }

  const objText = obj.join("\n") + "\n";
  const mtlText = mtl.join("\n") + "\n";

  return {
    obj: objText,
    mtl: mtlText,
    info: {
      verts: totalVerts,
      prims: totalPrims,
      mats: opts.vertexColors ? 1 : matName.size,
      nodes: grouped ? 1 : pieces.length,
      bytes: objText.length + mtlText.length,
    },
  };
}
